import { useEffect, useRef } from "react";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ATTRS = ["placeholder", "alt", "aria-label", "title"];
const SKIP_SELECTOR = "script,style,noscript,code,svg,textarea,[data-no-translate],[translate='no']";
const CHUNK = 60;
const hasLetters = (s) => /\p{L}/u.test(s);

const memCache = {};
function getCache(lang) {
  if (!memCache[lang]) {
    localStorage.removeItem(`gh_tr_${lang}`);
    try {
      memCache[lang] = JSON.parse(localStorage.getItem(`gh_tr_v2_${lang}`) || "{}");
    } catch {
      memCache[lang] = {};
    }
  }
  return memCache[lang];
}
function saveCache(lang) {
  try {
    localStorage.setItem(`gh_tr_v2_${lang}`, JSON.stringify(memCache[lang]));
  } catch {
    /* storage full — keep in memory only */
  }
}

function wrap(original, translated) {
  const lead = original.match(/^\s*/)[0];
  const trail = original.match(/\s*$/)[0];
  return lead + translated + trail;
}

const SEP = "␟";
const INLINE = new Set(["SPAN", "A", "STRONG", "EM", "B", "I", "U", "SMALL", "MARK", "SUP", "SUB", "LABEL", "BR", "ABBR", "TIME"]);

const hasDirectText = (el) =>
  Array.from(el.childNodes).some((n) => n.nodeType === 3 && n.nodeValue.trim());

// Merge an inline element into its parent's sentence only when the parent has its own text.
function blockOf(el) {
  let cur = el;
  while (cur.parentElement && INLINE.has(cur.tagName) && hasDirectText(cur.parentElement)) cur = cur.parentElement;
  return cur;
}

// A unit is one sentence (block) made of one or more text nodes; key = trimmed parts joined by SEP.
function collect(lang, textState, attrState) {
  const items = [];
  const groups = new Map();
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    const parent = node.parentElement;
    if (!parent || parent.closest(SKIP_SELECTOR)) continue;
    let st = textState.get(node);
    if (!st || node.nodeValue !== st.shown) {
      st = { orig: node.nodeValue, shown: null, lang: null };
      textState.set(node, st);
    }
    if (!st.orig.trim()) continue;
    const block = blockOf(parent);
    if (!groups.has(block)) groups.set(block, []);
    groups.get(block).push({ node, st });
  }
  groups.forEach((members) => {
    if (members.every((m) => m.st.lang === lang)) return;
    const parts = members.map((m) => m.st.orig.trim());
    if (!parts.some(hasLetters)) return;
    items.push({
      core: parts.join(SEP),
      apply: (tr) => {
        let out = tr.split(SEP);
        if (out.length !== members.length) out = [tr.split(SEP).join(" "), ...members.slice(1).map(() => "")];
        members.forEach((m, i) => {
          const part = out[i].trim();
          let v = part ? wrap(m.st.orig, part) : "";
          if (lang === "he" && /((^|\s)[\u05d0-\u05ea]|־)$/.test(part)) v = v.replace(/\s+$/, "");
          m.st.shown = v;
          m.st.lang = lang;
          m.node.nodeValue = v;
        });
      },
    });
  });
  document.body.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(",")).forEach((el) => {
    if (el.closest("[data-no-translate],[translate='no']")) return;
    let map = attrState.get(el);
    if (!map) attrState.set(el, (map = {}));
    ATTRS.forEach((attr) => {
      const cur = el.getAttribute(attr);
      if (cur == null) return;
      let st = map[attr];
      if (!st || cur !== st.shown) map[attr] = st = { orig: cur, shown: null, lang: null };
      if (st.lang === lang || !hasLetters(st.orig)) return;
      items.push({
        core: st.orig.trim(),
        apply: (tr) => {
          st.shown = tr;
          st.lang = lang;
          el.setAttribute(attr, tr);
        },
      });
    });
  });
  return items;
}

function applyCached(items, lang) {
  const cache = getCache(lang);
  items.forEach((it) => {
    if (cache[it.core] != null) it.apply(cache[it.core]);
  });
}

function restore(textState, attrState) {
  textState.forEach((st, node) => {
    if (node.isConnected && st.lang && node.nodeValue === st.shown) node.nodeValue = st.orig;
  });
  attrState.forEach((map, el) => {
    Object.entries(map).forEach(([attr, st]) => {
      if (el.isConnected && st.lang && el.getAttribute(attr) === st.shown) el.setAttribute(attr, st.orig);
    });
  });
  textState.clear();
  attrState.clear();
}

export const DomTranslator = () => {
  const { lang, setBusy } = useLanguage();
  const textState = useRef(new Map());
  const attrState = useRef(new Map());
  const inflight = useRef(new Map());

  useEffect(() => {
    if (lang === "en") {
      restore(textState.current, attrState.current);
      setBusy(false);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let retries = 0;

    const run = async () => {
      if (cancelled) return;
      const items = collect(lang, textState.current, attrState.current);
      applyCached(items, lang);
      const cache = getCache(lang);
      const missing = [...new Set(items.map((i) => i.core))].filter((c) => cache[c] == null);
      if (!missing.length) {
        if (!inflight.current.size) setBusy(false);
        return;
      }
      setBusy(true);
      const fresh = missing.filter((c) => !inflight.current.has(`${lang}:${c}`));
      for (let i = 0; i < fresh.length; i += CHUNK) {
        const texts = fresh.slice(i, i + CHUNK);
        const p = axios
          .post(`${API}/translate`, { lang, texts })
          .then(({ data }) => {
            Object.assign(cache, data.translations);
            saveCache(lang);
          })
          .catch(() => null)
          .finally(() => texts.forEach((c) => inflight.current.delete(`${lang}:${c}`)));
        texts.forEach((c) => inflight.current.set(`${lang}:${c}`, p));
      }
      await Promise.all(new Set(missing.map((c) => inflight.current.get(`${lang}:${c}`)).filter(Boolean)));
      if (cancelled) return;
      applyCached(collect(lang, textState.current, attrState.current), lang);
      if (missing.some((c) => cache[c] == null) && retries < 3) {
        retries += 1;
        setTimeout(schedule, 3000 * retries);
      }
      if (!inflight.current.size) setBusy(false);
    };

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(run, 80);
    };
    const isIgnored = (m) => {
      const el = m.target.nodeType === 1 ? m.target : m.target.parentElement;
      return el && el.closest("[data-no-translate]");
    };
    const observer = new MutationObserver((list) => {
      if (list.some((m) => !isIgnored(m))) schedule();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ATTRS,
    });
    run();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [lang, setBusy]);

  return null;
};
